"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, Layers, Flame, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface OverviewCopy {
  badge: string;
  verifiedLabel: string;
  heading: string;
  lead: string;
  point1Title: string;
  point1Desc: string;
  point2Title: string;
  point2Desc: string;
  point3Title: string;
  point3Desc: string;
  ctaTechData: string;
  imgAlt: string;
}
const copyData: Record<Lang, OverviewCopy> = {
  en: {
    badge: "Process Gas Filtration",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "All-Metal Filter Media Engineered for Severe Thermal & Aerodynamic Loads",
    lead: "High-temperature gas processing requires all-metal filter media that resist thermal shock, maintain aperture integrity under high face velocities, and withstand cyclic pulse-jet backwash without fiber shedding.",
    point1Title: "Continuous Thermal & Oxidation Resistance",
    point1Desc: "310S and high-nickel alloys provide operational stability under elevated thermal conditions in furnace exhaust and catalyst recovery streams.",
    point2Title: "Zero Media Migration Under High Velocity",
    point2Desc: "Rigid sintered laminates and compacted Dutch weaves prevent wire movement and fiber shedding in turbulent gas lines.",
    point3Title: "Complete Pulse-Jet Cleanability",
    point3Desc: "Robust mechanical construction withstands cyclic reverse pressure pulses for extended on-stream operating life.",
    ctaTechData: "Explore Technical Resources",
    imgAlt: "High-temperature industrial gas filtration stainless steel wire mesh assembly"
  },
  zh: {
    badge: "工艺气体过滤技术",
    verifiedLabel: "ISO 9044 / ASTM E2016 规范",
    heading: "全金属丝网滤材 专为严苛热载荷与高气速工况打造",
    lead: "高温气体工艺要求滤材具备抗热震冲击能力，在高面风速下保持孔径稳定，并能耐受周期性脉冲反吹而不脱落任何纤维杂质。",
    point1Title: "高温抗氧化与热循环稳定性",
    point1Desc: "310S 与耐热镍合金在高温炉气与催化剂回收工况下保持稳定运行。",
    point2Title: "高气速冲刷下零介质脱落",
    point2Desc: "刚性多层烧结复合板与紧密密纹网杜绝纤维滑移脱落，保障下游设备绝对洁净。",
    point3Title: "完全耐受脉冲在线反吹再生",
    point3Desc: "坚固的全金属机械强度能够长期承受反复逆向高压脉冲，大幅延长滤芯在线寿命。",
    ctaTechData: "查阅技术规格资料",
    imgAlt: "工业高温气体过滤不锈钢与耐热合金丝网组件"
  },
  es: {
    badge: "Filtración de gases de proceso",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Medios filtrantes totalmente metálicos diseñados para cargas térmicas y aerodinámicas severas",
    lead: "El procesamiento de gases a alta temperatura requiere medios totalmente metálicos que resistan choques térmicos, mantengan aperturas estables a altas velocidades y soporten limpieza por pulsos sin desprendimiento de fibras.",
    point1Title: "Resistencia térmica y a la oxidación continua",
    point1Desc: "310S y aleaciones de níquel aseguran estabilidad operativa en condiciones de alta temperatura en escapes de hornos y recuperación de catalizadores.",
    point2Title: "Cero migración de fibras a alta velocidad",
    point2Desc: "Laminados sinterizados rígidos y tejidos Dutch compactos previenen el desprendimiento en líneas de gas turbulentas.",
    point3Title: "Total aptitud para limpieza por pulsos",
    point3Desc: "Construcción mecánica robusta que tolera pulsos cíclicos de contrapresión para una vida útil prolongada.",
    ctaTechData: "Explorar recursos técnicos",
    imgAlt: "Conjunto de malla de alambre de acero inoxidable para filtración industrial de gas a alta temperatura"
  },
  de: {
    badge: "Prozessgasfiltration",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Vollmetallische Filtermedien für extreme thermische und aerodynamische Belastungen",
    lead: "Die Hochtemperatur-Gasbehandlung erfordert allmetallische Filtermedien, die thermoschockbeständig sind, Porenweiten bei hohen Anströmgeschwindigkeiten stabil halten und zyklischem Pulse-Jet-Abreinigen standhalten.",
    point1Title: "Kontinuierliche Temperatur- & Oxidationsbeständigkeit",
    point1Desc: "310S und Nickelbasislegierungen bieten Betriebsbeständigkeit bei hohen Temperaturen in Ofenabgasen und Katalysatorrückgewinnung.",
    point2Title: "Keine Medienmigration bei hohen Strömungsgeschwindigkeiten",
    point2Desc: "Starre Sinterlaminate und verdichtete Tressengewebe verhindern Drahtverschiebung und Partikelabrieb in Gasleitungen.",
    point3Title: "Vollständige Pulse-Jet-Abreinigbarkeit",
    point3Desc: "Robuster mechanischer Aufbau hält zyklischen Rückspülimpulsen für maximale Standzeiten stand.",
    ctaTechData: "Technische Ressourcen ansehen",
    imgAlt: "Edelstahldrahtgewebe-Baugruppe für Hochtemperatur-Gasfiltration in der Industrie"
  },
  fr: {
    badge: "Filtration des gaz de procédé",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Médias filtrants tout métal conçus pour contraintes thermiques et aérodynamiques sévères",
    lead: "Le traitement des gaz à haute température nécessite des toiles tout métal résistant aux chocs thermiques, conservant leur porosité sous forte vitesse et tolérant le décolmatage pneumatique sans relargage de fibres.",
    point1Title: "Tenue continue en température et à l'oxydation",
    point1Desc: "Le 310S et les alliages de nickel assurent une stabilité en service à haute température dans les fumées de four et la récupération de catalyseur.",
    point2Title: "Zéro migration de média sous grande vitesse",
    point2Desc: "Les laminés frittés rigides et tissages hollandais compacts éliminent tout glissement de fil dans les flux turbulents.",
    point3Title: "Aptitude complète au décolmatage pneumatique",
    point3Desc: "Une structure mécanique robuste supporte les impulsions de contre-pression répétées pour une longévité accrue.",
    ctaTechData: "Consulter les ressources techniques",
    imgAlt: "Ensemble filtrant en toile métallique pour filtration de gaz industriels à haute température"
  },
  pt: {
    badge: "Filtração de gases de processo",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Meios filtrantes metálicos projetados para severas cargas térmicas e aerodinâmicas",
    lead: "O processamento de gases em alta temperatura exige meios totalmente metálicos resistentes ao choque térmico, mantendo aberturas estáveis sob altas velocidades e suportando pulso de ar sem liberação de fibras.",
    point1Title: "Resistência contínua à temperatura e oxidação",
    point1Desc: "310S e ligas de níquel garantem estabilidade operacional em condições de alta temperatura em exaustão de fornos e recuperação de catalisadores.",
    point2Title: "Zero migração de meio sob alta velocidade",
    point2Desc: "Laminados sinterizados rígidos e tramas Dutch compactadas evitam desprendimento em correntes turbulentas.",
    point3Title: "Total capacidade de limpeza por pulso reverso",
    point3Desc: "Construção mecânica robusta que suporta pulsos cíclicos de contrapressão para vida útil estendida.",
    ctaTechData: "Consultar recursos técnicos",
    imgAlt: "Conjunto de tela metálica de aço inoxidável para filtração industrial de gases em alta temperatura"
  }
};
export default function GasAirOverview() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const keyPoints = [{
    icon: Flame,
    title: c.point1Title,
    desc: c.point1Desc
  }, {
    icon: Layers,
    title: c.point2Title,
    desc: c.point2Desc
  }, {
    icon: ShieldCheck,
    title: c.point3Title,
    desc: c.point3Desc
  }];
  return <section id="gasairoverview" data-nav="light" className="bg-muted/50 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left 6 Columns: Large Industrial Photo (no overlay, framed, subtle zoom) */}
          <div className="lg:col-span-6">
            <Reveal className="w-full">
              <div className="mf-media-framed mf-ratio-card w-full mf-img-zoom">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/petrochemical_filtration_mesh.webp-wvc-sizes" alt={c.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
            </Reveal>
          </div>

          {/* Right 6 Columns: Narrative & Exactly Three Key Points */}
          <Reveal delay={80} className="lg:col-span-6 flex flex-col justify-center">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm tracking-wide bg-card text-foreground">
                {c.badge}
              </Badge>
              <span className="text-sm font-mono text-muted-foreground">
                {c.verifiedLabel}
              </span>
            </div>

            <h2 className="font-default tracking-tight text-foreground mf-h2">
              {c.heading}
            </h2>

            <p className="mt-4 text-muted-foreground mf-lead max-w-[65ch]">
              {c.lead}
            </p>

            {/* Exactly Three Plain Key Points */}
            <div className="mt-6 flex flex-col gap-3.5">
              {keyPoints.map((point, index) => {
              const IconComponent = point.icon;
              return <Reveal key={point.title} delay={120 + index * 80}>
                    <div className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border">
                      <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-default font-semibold text-base text-foreground leading-snug">
                          {point.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                          {point.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>;
            })}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 border-border bg-card hover:bg-muted font-semibold">
                <Link to="/TechnicalResourcesPage">
                  {c.ctaTechData}
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}